import { useEffect, useRef, useState } from "react";

export type WorkshopLocation = {
  id: string;
  naam: string;
  adres: string;
  stad: string;
  typeRuimte: string;
  capaciteit: number | null;
  lat: number | null;
  lng: number | null;
};

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ok"; locations: WorkshopLocation[] };

const ENDPOINT = import.meta.env.VITE_WORKSHOPS_API_URL ?? "";
const REVALIDATE_INTERVAL_MS = 60_000;

export function useWorkshopLocations(): State {
  const [state, setState] = useState<State>({ status: "loading" });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!ENDPOINT) {
      setState({ status: "ok", locations: [] });
      return;
    }

    const fetchLocations = async () => {
      try {
        const url = `${ENDPOINT}/locations?t=${Date.now()}`;
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as { locations: WorkshopLocation[] };
        if (mountedRef.current) {
          setState({ status: "ok", locations: json.locations ?? [] });
        }
      } catch {
        if (mountedRef.current) setState({ status: "error" });
      }
    };

    fetchLocations();

    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchLocations();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", fetchLocations);
    const intervalId = window.setInterval(fetchLocations, REVALIDATE_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", fetchLocations);
      window.clearInterval(intervalId);
    };
  }, []);

  return state;
}
